import Title from '@cohort/components-xps/components/Title';
import {defaultErrorMessage} from '@cohort/shared/models';
import type {
  CohortFormConfig,
  CohortFormQuestion,
} from '@cohort/shared/schema/apps/cohort/perks/form';
import type {LocalizationConfig} from '@cohort/shared/schema/common';
import {EmailSchema} from '@cohort/shared/schema/common';
import {
  buildLocalizationConfig,
  formatI18nLanguage,
  getLocalizedContentOrThrow,
} from '@cohort/shared/utils/localization';
import {isCohortError} from '@cohort/shared-frontend/common/errors';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import type {PerkUsageComponentProps} from '@cohort/wallet/apps/PerkIntegration';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import type {SelectOption} from '@cohort/wallet/components/forms/DropDownInput';
import {DropDownInput} from '@cohort/wallet/components/forms/DropDownInput';
import {Input} from '@cohort/wallet/components/forms/Input';
import {Transition} from '@cohort/wallet/components/transitions/Transition';
import {useCohortMutation} from '@cohort/wallet/hooks/api/Query';
import useNotify from '@cohort/wallet/hooks/notify';
import {useApi} from '@cohort/wallet/hooks/useApi';
import {useMerchantContext} from '@cohort/wallet/hooks/useMerchantContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {OwnershipsApi} from '@cohort/wallet/lib/Endpoints';
import i18n from 'i18next';
import React, {Fragment, useCallback, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

export const CohortFormPerkUsageComponent: React.FC<PerkUsageComponentProps> = ({
  access,
  ownership,
  onPerkUsageSuccess,
}) => {
  const merchant = useMerchantContext();
  const {t} = useTranslation('app-cohort', {keyPrefix: 'perkIntegrations.form.perkUsageComponent'});
  const {register, handleSubmit: onFormSubmit, setValue, getValues} = useForm();
  const doUsePerkAccess = useApi(OwnershipsApi.doUsePerkAccess);
  const notify = useNotify();

  const perkConfig = access.perk.integration?.config as CohortFormConfig;
  const {accentColor, hasDarkBg} = useThemeContext();

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const localizationConfig = buildLocalizationConfig(
    formatI18nLanguage(i18n.language),
    merchant.supportedLanguages,
    merchant.defaultLanguage
  );

  const {
    isLoading,
    isSuccess,
    mutate: doUsePerkAccessMutation,
  } = useCohortMutation({
    mutationFn: async (usageData: unknown) =>
      doUsePerkAccess(ownership.id, access.id, null, usageData),
    onSuccess: () => onPerkUsageSuccess(),
    onError: err => {
      if (isCohortError(err, 'perk.usages-limit-reached')) {
        notify('error', t('maxUsagesReached'), {autoClose: false});
        return;
      }
      notify('error', defaultErrorMessage);
    },
  });

  const renderError = useCallback(
    (questionId: string): JSX.Element => {
      if (formErrors[questionId] === undefined) {
        return <Fragment />;
      }

      return (
        <Transition appear enterFrom="bottom" show twClasses="text-red-500 text-sm mt-1">
          <Fragment>{formErrors[questionId]}</Fragment>
        </Transition>
      );
    },
    [formErrors]
  );

  const renderInput = useCallback(
    (
      question: CohortFormQuestion,
      index: number,
      localizationConfig: LocalizationConfig
    ): JSX.Element => {
      const localizedOptions =
        question.options?.map(option => {
          return {
            value: option.value,
            label: getLocalizedContentOrThrow(option.label, localizationConfig),
          };
        }) ?? null;
      const localizedName = getLocalizedContentOrThrow(question.name, localizationConfig);

      switch (question.type) {
        case 'text':
          return (
            <div className="flex flex-col">
              <label className="mb-1 text-sm">
                {localizedName}
                {question.mandatory && <span>*</span>}
              </label>

              <Input
                testId={`perk-form-input-${index}`}
                name="perk-usage-input"
                type="text"
                accentColor={accentColor}
                hasDarkBg={hasDarkBg}
                required={question.mandatory}
                register={register(question.id)}
              />
              {renderError(question.id)}
            </div>
          );
        case 'select':
          if (localizedOptions === null) {
            throw new Error('No options for select');
          }
          return (
            <Fragment>
              <DropDownInput
                label={`${localizedName} ${question.mandatory ? '*' : ''}`}
                value={{
                  value: getValues(question.id) ?? '',
                  label: getValues(question.id) ?? t('dropdownDefaultValue'),
                }}
                options={localizedOptions}
                onChange={(selectedValue: SelectOption) =>
                  setValue(question.id, selectedValue.value)
                }
              />
              {renderError(question.id)}
            </Fragment>
          );
        case 'checkbox':
          return (
            <Fragment>
              <label className="cursor-pointer">
                <input
                  data-testid={`perk-form-checkbox-${index}`}
                  type="checkbox"
                  {...register(question.id)}
                  className={classNames(
                    'h-4 w-4 cursor-pointer rounded border focus:outline-none focus:ring-0 focus:ring-offset-0',
                    hasDarkBg ? 'border-white/10 bg-white/5' : 'border-black/10 bg-black/5'
                  )}
                  style={{color: accentColor}}
                />
                <span className="ml-2 text-sm">{localizedName}</span>
                {question.mandatory && <span> *</span>}
              </label>
              {renderError(question.id)}
            </Fragment>
          );
        case 'email':
          return (
            <div className="flex flex-col">
              <label className="mb-1 text-sm">
                {localizedName} {question.mandatory && <span>*</span>}
              </label>

              <Input
                testId={`perk-form-email-input-${index}`}
                name="perk-usage-email-input"
                type="email"
                accentColor={accentColor}
                hasDarkBg={hasDarkBg}
                required={question.mandatory}
                register={register(question.id)}
              />
              {renderError(question.id)}
            </div>
          );
        default:
          throw new Error('Unsupported question type');
      }
    },
    [accentColor, getValues, hasDarkBg, register, renderError, setValue, t]
  );

  const onSubmit = useCallback(
    (data: Record<string, string | undefined>): void => {
      const values: Record<string, string | undefined> = {};
      const submitErrors: Record<string, string> = {};
      for (const question of perkConfig.questions) {
        values[question.id] = data[question.id];

        if (question.mandatory && (data[question.id] ?? '').length === 0) {
          submitErrors[question.id] = t('required');
        }

        if (question.type === 'email' && (data[question.id] ?? '').length > 0) {
          try {
            EmailSchema.parse(data[question.id] ?? '');
          } catch {
            submitErrors[question.id] = t('invalidEmail');
          }
        }

        if (question.type === 'checkbox' && question.mandatory && !data[question.id]) {
          submitErrors[question.id] = t('checkboxRequired');
        }
      }

      if (Object.keys(submitErrors).length > 0) {
        setFormErrors(submitErrors);
        return;
      }

      doUsePerkAccessMutation({values});
    },
    [doUsePerkAccessMutation, perkConfig.questions, t]
  );

  return (
    <Fragment>
      <Title
        content={access.perk.displayName}
        testId="use-perk-form-title"
        classes="text-center md:text-left"
      />

      <div
        dangerouslySetInnerHTML={{
          __html: getLocalizedContentOrThrow(perkConfig.description, localizationConfig),
        }}
        className={classNames('prose mt-2', hasDarkBg && 'prose-invert')}
      />

      <form className="mt-3 max-w-3xl space-y-5 px-[1px]">
        {perkConfig.questions.map((question, index) => {
          return <div key={question.id}>{renderInput(question, index, localizationConfig)}</div>;
        })}
        <PrimaryButton
          testId="perk-form-submit"
          text={t('submit')}
          className="w-full"
          onClick={onFormSubmit(onSubmit)}
          isLoading={isLoading || isSuccess}
          tracking={{
            namespace: 'perks.usage',
            metadata: {
              appId: access.perk.integration?.perkIntegrationId,
              action: 'linkAccount',
              ownershipId: ownership.id,
              accessId: access.id,
              perkId: access.perk.id,
            },
          }}
        />
      </form>
    </Fragment>
  );
};
